import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { colors2, SkeletonLoader } from "oolib";
import { UI_CAPTION, UI_BODY_SM } from "oolib/dist/v2/components/Typo2";
import { CONFIG } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/CONFIG";
import { TriageField } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/TriageField";
import { PrioritiesTableV2 } from "./PrioritiesTableV2";
import { ZoneStatsBar } from "./ZoneStatsBar";
import { ActFirstPanel, rankActFirst } from "./ActFirstPanel";
import { useFillScrollParentHeight } from "./useFillScrollParentHeight";
import { PillSwitch } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/PillSwitch";
import { momentumReason, momentumBasisText, grainNoun } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/format";
import { isSinglePeriod, usePrioritiesScoring } from "./usePrioritiesScoring";
import { CalendarPlus } from "@phosphor-icons/react";
import { SM_UIContent } from "@/_self_managed/SM_UIContent";

/**
 * The Priorities view: the header controls, the zone bar, then the field
 * with the act-first list or the ranked table. The requests and the scoring
 * live in usePrioritiesScoring.
 *
 * The Priorities page shows every section; the report widget (WidgetTemp_SHI
 * → Priorities) turns them on and off per author through `sections`.
 */

// Every section on, the page's reading. A caller passes only the ones it
// wants off.
const ALL_SECTIONS = {
  trendWindow: true,
  grainSwitch: true,
  zoneBar: true,
  actFirst: true,
  // The table's Priorities / Wins switch.
  tableTabs: true,
};
// The field row's height when it does not fill the page: the chart's own
// fixed height (TriageField), so the act-first list beside it lines up.
const FIELD_ROW_HEIGHT = 420;

// The latest calendar month among the period options — where Priorities
// lands by default, since it compares periods. null when there is none.
export const latestMonthOf = (periodOptions = []) => {
  const months = periodOptions.filter((o) => /^month_\d{4}_\d{2}$/.test(o.value)).sort((a, b) => a.value.localeCompare(b.value));
  return months[months.length - 1] || null;
};
// V1's header controls: how many periods the trend spans, and whether the
// field scores sub-topics or whole topics.
const WINDOWS = CONFIG.TREND_WINDOW_OPTIONS.map((n) => ({ value: String(n), display: String(n) }));
const GRAINS = [
  { value: "findings", display: "Sub-topics" },
  { value: "buckets", display: "Topics" },
];
const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;
const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

// The field's placeholder while its two requests are in flight: the same
// box the chart draws in, a faint axis on each edge and a scatter of
// bubbles where the data will land — so the page reads as "a chart is
// coming", not as a list.
const FieldShell = styled.div`
  position: relative;
  height: ${FIELD_ROW_HEIGHT}px;
  border: 1px solid ${colors2.grey10};
  border-radius: 12px;
  background: ${colors2.white};
  overflow: hidden;
`;
const Axis = styled.div`
  position: absolute;
  background: ${colors2.grey10};
`;
const SKELETON_BUBBLES = [
  { x: 62, y: 18, r: 34 }, { x: 70, y: 30, r: 22 }, { x: 55, y: 42, r: 16 }, { x: 40, y: 58, r: 26 },
  { x: 48, y: 70, r: 14 }, { x: 30, y: 76, r: 18 }, { x: 78, y: 62, r: 12 }, { x: 24, y: 40, r: 11 },
  { x: 58, y: 80, r: 9 }, { x: 68, y: 48, r: 10 },
];
const FieldSkeleton = () => (
  <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
    <SkeletonLoader style={{ height: "1.1rem", width: "38rem", maxWidth: "100%", borderRadius: 4, opacity: 0.6 }} />
    <FieldShell>
      <Axis style={{ left: 40, top: 16, bottom: 32, width: 1 }} />
      <Axis style={{ left: 40, right: 24, bottom: 32, height: 1 }} />
      {SKELETON_BUBBLES.map((b, i) => (
        <SkeletonLoader
          key={i}
          style={{ position: "absolute", left: `${b.x}%`, top: `${b.y}%`, width: b.r * 2, height: b.r * 2, borderRadius: "50%", opacity: 0.35, transform: "translate(-50%, -50%)" }}
        />
      ))}
    </FieldShell>
  </div>
);

export const PrioritiesLayoutV2 = ({
  // "field": the bubbles + act-first list; "table": the ranked table.
  mode = "field",
  topicClusteringLens,
  historyFilters,
  period,
  // The period compared with, by scorer key (the page's shared period
  // selection). Given, it decides every comparison and the trend-window
  // pills step aside; the report widget passes none and keeps them.
  compareKey,
  dateFieldMap,
  signalMetaById,
  titleByContentType,
  onRowClick,
  openRowNodeId,
  // The parent keeps the tenant's signal meta + category contract from
  // whichever counts response arrived first; this layout's own response
  // feeds it too, so landing here directly still colours everything.
  onCountsMeta,
  // What the scorer settled on for this scope (scored.meta: mode, how many
  // earlier periods it drew on…) — null while loading. The page uses it to
  // lock the "vs" period while history mode ignores it.
  onScoreMeta,
  // Which sections render — see ALL_SECTIONS. Partial: unnamed ones stay on.
  sections: sectionsOverride,
  // Where the field starts scoring: "findings" (sub-topics) or "buckets"
  // (topics). What the viewer is stuck with when the grain switch is off.
  initialScoreGrain = "findings",
  // How many periods the trend spans to begin with — the pills move it.
  initialTrendWindow = CONFIG.TREND_WINDOW,
  // Which list the table opens on: "priorities" or "wins".
  initialTableTab = "priorities",
  // Table columns to leave out, by colId — see PrioritiesTableV2's
  // COLUMN_SPECS. The page shows them all.
  hiddenTableColumns,
  // The field row fills the page's remaining height (the Priorities page)
  // or takes the chart's fixed height (inside a report widget).
  fillHeight = true,
  // Told when the requests + scoring start and finish — the report widget
  // reports that to the loaded-widgets tracker.
  onLoadingChange,
}) => {
  const sections = { ...ALL_SECTIONS, ...sectionsOverride };
  const [scoreGrain, setScoreGrain] = useState(initialScoreGrain);
  // The page lands on two periods (the selected one and the one before it).
  const [trendWindow, setTrendWindow] = useState(initialTrendWindow);
  // The zone picked on the stats bar; null = the whole field. Picking
  // another swaps, picking the same one again clears.
  const [zoneFilter, setZoneFilter] = useState(null);
  const toggleZone = (zone) => setZoneFilter((prev) => (prev === zone ? null : zone));
  // Measured only when filling: with no ref attached the hook idles.
  const { ref: fieldRowRef, height: measuredRowHeight } = useFillScrollParentHeight();
  const fieldRowHeight = fillHeight ? measuredRowHeight : null;

  const { scored, loading } = usePrioritiesScoring({
    topicClusteringLens,
    historyFilters,
    period,
    dateFieldMap,
    signalMetaById,
    scoreGrain,
    trendWindow,
    compareKey,
    onCountsMeta,
  });
  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);
  useEffect(() => {
    onScoreMeta?.(loading ? null : scored?.meta || null);
  }, [loading, scored, onScoreMeta]);

  if (loading) return <FieldSkeleton />;
  const { priorities, wins, meta } = scored;
  // One month of data and nothing before it: no momentum, so the zone strip
  // and the field sit blurred behind a note that says what would fill them.
  const singlePeriod = isSinglePeriod(meta);
  const noteFor = (text) => (singlePeriod ? { blurNote: text, blurNoteIcon: CalendarPlus } : {});
  const zonesNote = noteFor(SM_UIContent.SM_Dashboard.singlePeriod.zones);
  // What the field, the Act-first list and the table's Priorities tab work
  // from: the zone the bar picked, or everything.
  const fieldItems = zoneFilter ? priorities.filter((item) => item.zone === zoneFilter) : priorities;
  // The act-first sub-topics: the only bubbles that carry a label, and the
  // ones the field keeps at full strength while the rest fade.
  const actFirstIds = new Set(rankActFirst(fieldItems).map((item) => item.nodeId));
  if (!priorities.length) {
    return <UI_BODY_SM style={{ color: colors2.grey60 }}>Nothing to score in this scope.</UI_BODY_SM>;
  }
  const summary = !meta.momentumAvailable
    ? momentumReason(null, meta)
    : `${meta.nowLabel} · ${momentumBasisText(meta)} · ${meta.totalNow} observation${meta.totalNow === 1 ? "" : "s"} in this period`;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <HeaderRow>
        <UI_CAPTION style={{ color: meta.momentumAvailable ? colors2.grey60 : colors2.grey80 }}>{summary}</UI_CAPTION>
        {(sections.trendWindow || sections.grainSwitch) && (
          <Controls>
            {sections.trendWindow && compareKey === undefined && (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <UI_CAPTION style={{ color: colors2.grey60 }}>Trend over</UI_CAPTION>
                <PillSwitch options={WINDOWS} value={String(trendWindow)} onChange={(v) => setTrendWindow(Number(v))} />
                <UI_CAPTION style={{ color: colors2.grey60 }}>{grainNoun(meta.timeGrain)}</UI_CAPTION>
              </div>
            )}
            {sections.grainSwitch && <PillSwitch options={GRAINS} value={scoreGrain} onChange={setScoreGrain} />}
          </Controls>
        )}
      </HeaderRow>
      {sections.zoneBar && <ZoneStatsBar items={priorities} selected={zoneFilter} onToggle={toggleZone} {...zonesNote} />}
      {mode === "table" ? (
        <PrioritiesTableV2
          priorities={fieldItems}
          wins={wins}
          meta={meta}
          titleByContentType={titleByContentType}
          onRowClick={onRowClick}
          initialTab={initialTableTab}
          showTabs={sections.tableTabs}
          hiddenColumns={hiddenTableColumns}
        />
      ) : (
        <div
          ref={fillHeight ? fieldRowRef : undefined}
          style={{ display: "flex", gap: 16, alignItems: "stretch", height: fieldRowHeight ?? (fillHeight ? undefined : FIELD_ROW_HEIGHT) }}
        >
          <div style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
            <TriageField
              items={fieldItems}
              labelIds={actFirstIds}
              meta={meta}
              titleByContentType={titleByContentType}
              onRowClick={onRowClick}
              openRowNodeId={openRowNodeId}
              fill={fieldRowHeight !== null}
              focusIds={actFirstIds}
              {...zonesNote}
            />
          </div>
          {sections.actFirst && (
            <ActFirstPanel items={fieldItems} plottedCount={fieldItems.length} totalCount={priorities.length} onRowClick={onRowClick} />
          )}
        </div>
      )}
    </div>
  );
};
